import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const CRM_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/images/cropped-logo-szpital_akredytacja.png`;
const CRM_BRAND_ALT = 'CRM Szpital w Łapach – logo';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const RECAPTCHA_ACTION = 'doctor_absence_submit';

const apiBaseUrl = (() => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.startsWith('http')) return envUrl;
  return '/api';
})();

const PublicDoctorAbsence = () => {
  const [form, setForm] = useState({
    pwzNumber: '',
    startDate: '',
    endDate: '',
    notes: '',
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaReady, setRecaptchaReady] = useState(!RECAPTCHA_SITE_KEY);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    if (window.grecaptcha) {
      setRecaptchaReady(true);
      return;
    }
    const failOpenTimer = window.setTimeout(() => {
      setRecaptchaReady(true);
    }, 12000);
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          window.clearTimeout(failOpenTimer);
          setRecaptchaReady(true);
        });
      } else {
        window.clearTimeout(failOpenTimer);
        setRecaptchaReady(true);
      }
    };
    script.onerror = () => {
      window.clearTimeout(failOpenTimer);
      setRecaptchaReady(true);
    };
    document.head.appendChild(script);
    return () => window.clearTimeout(failOpenTimer);
  }, []);

  const validate = () => {
    if (!/^[0-9]{7}$/.test(form.pwzNumber.trim())) {
      return 'Numer prawa wykonywania zawodu musi składać się z 7 cyfr.';
    }
    if (!form.startDate || !form.endDate) {
      return 'Podaj daty rozpoczęcia i zakończenia nieobecności.';
    }
    if (form.endDate < form.startDate) {
      return 'Data zakończenia nie może być wcześniejsza niż data rozpoczęcia.';
    }
    if (form.notes && form.notes.length > 2000) {
      return 'Uwagi mogą zawierać maksymalnie 2000 znaków.';
    }
    if (!form.consent) {
      return 'Aby wysłać zgłoszenie, musisz potwierdzić prawdziwość podanych danych.';
    }
    return null;
  };

  const getRecaptchaToken = async () => {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return '';
    try {
      return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION });
    } catch (e) {
      return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken();
      await axios.post(`${apiBaseUrl}/public/doctor-absences`, {
        pwzNumber: form.pwzNumber.trim(),
        startDate: form.startDate,
        endDate: form.endDate,
        notes: form.notes ? form.notes.trim() : null,
        recaptchaToken,
      });
      setSuccess(true);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (status === 429) {
        setError(msg || 'Przekroczono dozwoloną liczbę zgłoszeń. Spróbuj ponownie za godzinę.');
      } else if (status === 403) {
        setError(msg || 'Weryfikacja CAPTCHA nie powiodła się. Odśwież stronę i spróbuj ponownie.');
      } else if (status === 400) {
        setError(msg || 'Nieprawidłowe dane formularza.');
      } else {
        setError(msg || 'Wystąpił błąd. Spróbuj ponownie później.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-lg w-full bg-white shadow rounded-lg p-8 text-center">
          <img
            src={CRM_LOGO_SRC}
            alt={CRM_BRAND_ALT}
            className="mx-auto mb-6 block w-full max-w-md object-contain max-h-20 sm:max-h-24"
            loading="eager"
          />
          <CheckCircle className="mx-auto text-green-600" size={56} />
          <h1 className="text-2xl font-semibold mt-4 text-gray-900">Dziękujemy!</h1>
          <p className="mt-3 text-gray-600">
            Zgłoszenie nieobecności zostało przekazane do działu kadr. W razie pytań pracownik kadr skontaktuje się z Tobą telefonicznie lub mailowo.
          </p>
          <p className="mt-2 text-sm text-gray-500">Możesz zamknąć tę stronę.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white shadow rounded-lg overflow-hidden">
        <div className="bg-white border-b border-gray-100 px-4 sm:px-6 py-5 flex flex-col items-stretch justify-center">
          <img
            src={CRM_LOGO_SRC}
            alt={CRM_BRAND_ALT}
            className="w-full max-h-20 sm:max-h-24 md:max-h-28 object-contain object-center"
            loading="eager"
          />
          <p className="mt-3 text-sm text-gray-500 text-center">Oficjalny formularz Szpitala w Łapach (system CRM)</p>
        </div>
        <div className="bg-blue-600 text-white p-6">
          <h1 className="text-xl font-semibold">Zgłoszenie nieobecności lekarza</h1>
          <p className="text-blue-100 text-sm mt-1">Formularz dla lekarzy – zgłoszenie urlopu lub nieobecności</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numer prawa wykonywania zawodu (PWZ) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{7}"
              maxLength={7}
              required
              value={form.pwzNumber}
              onChange={(e) => setForm({ ...form, pwzNumber: e.target.value.replace(/[^0-9]/g, '') })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="np. 1234567"
            />
            <p className="text-xs text-gray-500 mt-1">7 cyfr nadanych przez Okręgową Izbę Lekarską</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data od <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data do <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Uwagi</label>
            <textarea
              rows={4}
              maxLength={2000}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Dodatkowe informacje (opcjonalnie)"
            />
            <p className="text-xs text-gray-500 mt-1">{form.notes.length} / 2000 znaków</p>
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="consent"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-1"
            />
            <label htmlFor="consent" className="text-sm text-gray-700">
              Oświadczam, że podane dane są zgodne z prawdą i jestem osobą uprawnioną do złożenia tego zgłoszenia.
            </label>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 flex items-start gap-2">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !recaptchaReady}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium py-2.5 rounded-md flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 size={18} className="animate-spin" />}
            {submitting ? 'Wysyłanie...' : 'Wyślij zgłoszenie'}
          </button>

          {RECAPTCHA_SITE_KEY && (
            <p className="text-xs text-gray-500 text-center">
              Strona chroniona przez reCAPTCHA (Google) –{' '}
              <a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Polityka prywatności</a>
              {' '}i{' '}
              <a className="underline" href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Warunki</a>.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default PublicDoctorAbsence;
